import process from 'node:process';
import React from 'react';
import { renderErrorSync } from '@wix/cli-ui-kit';
import { writeCrashReport } from '@wix/cli-debug-log';
import { createEnv } from '@wix/cli-env';
import { ErrorViewer } from './components/ErrorViewer.js';
import type { ErrorReporterOptions } from './error-reporter.js';
import { createErrorReporter } from './error-reporter.js';
import { collectUserFeedback } from './user-feedback.js';
import { RawErrorViewer } from './components/RawErrorViewer.js';

export type ErrorHandlerFn = (args: { error: unknown; data?: unknown }) => void;

export function createErrorHandler({
  debugOutputPath,
  userFeedbackCachePath,
  ...errorReporterOptions
}: {
  debugOutputPath: string;
  userFeedbackCachePath?: string;
} & ErrorReporterOptions) {
  const errorReporter = createErrorReporter(errorReporterOptions);

  const handler: ErrorHandlerFn = function ({ error, data }) {
    const reportResult = errorReporter.reportError(error, { data });
    const { isCI } = createEnv();
    const occuredAt = new Date();

    renderErrorSync(
      <>
        <ErrorViewer error={error} />
        {isCI && <RawErrorViewer error={error} occurredAt={occuredAt} />}
      </>
    );

    writeCrashReport(debugOutputPath, error, occuredAt);
    process.exitCode = 1;

    collectUserFeedback({
      reportResult,
      errorReporter,
      cachePath: userFeedbackCachePath,
    }).catch((e: unknown) => {
      writeCrashReport(debugOutputPath, e, new Date());
    });
  };

  const registerToProcessErrors = function () {
    process.on('uncaughtException', (error) => {
      handler({
        error,
        data: { mechanism: { handled: false, type: 'uncaughtException' } },
      });
    });

    process.on('unhandledRejection', (error) => {
      /**
       * Temporary workaround for https://github.com/vitejs/vite/issues/14706
       */
      if (
        error instanceof Error &&
        error.message.startsWith('Build failed with') &&
        error.stack?.includes('esbuild')
      ) {
        return;
      }

      /**
       * Workaround for PostCSS unhandled errors https://github.com/vitejs/vite/issues/18882
       */
      if (
        error instanceof Error &&
        error.message.startsWith('Failed to load PostCSS config')
      ) {
        return;
      }

      handler({
        error,
        data: { mechanism: { handled: false, type: 'unhandledRejection' } },
      });
    });
  };

  return {
    handler,
    errorReporter,
    registerToProcessErrors,
  };
}
